/**
 * What the twin calls an asset, wherever it names one: the HUD identity
 * card, the Health panel header, the dialog titles (health dashboard,
 * maintenance history, system report), the system report's AI analysis, the
 * Parts reservation note and the live feed's event log entries.
 *
 *   - title: the asset spec's label (a parametric antenna twin's spec carries
 *     the antenna's name, for example "Wallops 26m", while its element is the
 *     shared rf_antenna one), else the element's callsign, else the asset id;
 *   - subtitle: the element type, followed by the element's callsign when it
 *     differs from both the title and the type ("GOES-R Series, GOES
 *     Satellite"); null when that leaves nothing. Comparisons ignore case and
 *     surrounding spaces.
 */
import type { AssetSpec, Element } from '../types';

export interface AssetName {
  title: string;
  subtitle: string | null;
}

const present = (value: string | null | undefined): string | undefined => {
  const v = value?.trim();
  return v ? v : undefined;
};

const same = (a: string, b: string): boolean => a.toLowerCase() === b.toLowerCase();

export function assetName(spec: Pick<AssetSpec, 'label'> | null | undefined, element: Pick<Element, 'callsign' | 'type'> | null | undefined, assetId: string): AssetName {
  const label = present(spec?.label);
  const callsign = present(element?.callsign);
  const type = present(element?.type);
  const title = label ?? callsign ?? assetId;
  const parts: string[] = [];
  if (type) parts.push(type);
  if (callsign && !same(callsign, title) && !(type && same(callsign, type))) parts.push(callsign);
  return { title, subtitle: parts.length > 0 ? parts.join(', ') : null };
}
